import { Injectable, signal, effect } from "@angular/core";

import {
  PageSection,
  PageConfiguration,
  PageConfigurationMap,
} from "@shared/models/page-configuration.model";

@Injectable({
  providedIn: "root",
})
export class PageConfigurationService {
  private readonly STORAGE_KEY = "foresight_page_configs";

  private readonly pageDefaults = new Map<string, PageSection[]>();

  private readonly configurations = signal<PageConfigurationMap>(
    this.loadAllConfigurations(),
  );

  constructor() {
    effect(() => {
      const configs = this.configurations();
      this.saveAllConfigurations(configs);
    });
  }

  registerPageDefaults(pageId: string, defaultSections: PageSection[]): void {
    if (!this.pageDefaults.has(pageId)) {
      this.pageDefaults.set(pageId, defaultSections);

      const configs = this.configurations();
      if (!configs[pageId]) {
        this.configurations.update((current) => ({
          ...current,
          [pageId]: {
            sections: [...defaultSections],
            lastModified: new Date().toISOString(),
          },
        }));
      }
    }
  }

  getPageSections(pageId: string): PageSection[] {
    const config = this.configurations()[pageId];

    if (!config) {
      const defaults = this.pageDefaults.get(pageId) || [];
      return [...defaults];
    }

    return this.mergeWithDefaults(pageId, config.sections);
  }

  getEnabledSections(pageId: string): PageSection[] {
    return this.getPageSections(pageId)
      .filter((section) => section.enabled)
      .sort((a, b) => a.order - b.order);
  }

  updatePageConfiguration(pageId: string, sections: PageSection[]): void {
    const sortedSections = [...sections].sort((a, b) => a.order - b.order);

    this.configurations.update((current) => ({
      ...current,
      [pageId]: {
        sections: sortedSections,
        lastModified: new Date().toISOString(),
      },
    }));
  }

  resetPageToDefaults(pageId: string): void {
    const defaults = this.pageDefaults.get(pageId);

    if (defaults) {
      this.configurations.update((current) => {
        const updated = { ...current };
        delete updated[pageId];
        return updated;
      });
    }
  }

  resetAllToDefaults(): void {
    this.configurations.set({});
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear localStorage:", error);
    }
  }

  getPageConfiguration(pageId: string): PageConfiguration | null {
    return this.configurations()[pageId] || null;
  }

  private loadAllConfigurations(): PageConfigurationMap {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored) as PageConfigurationMap;
      }
    } catch (error) {
      console.warn(
        "Failed to load page configurations from localStorage:",
        error,
      );
    }

    return {};
  }

  private saveAllConfigurations(configs: PageConfigurationMap): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(configs));
    } catch (error) {
      console.error(
        "Failed to save page configurations to localStorage:",
        error,
      );
    }
  }

  private mergeWithDefaults(
    pageId: string,
    storedSections: PageSection[],
  ): PageSection[] {
    const defaults = this.pageDefaults.get(pageId);

    if (!defaults) {
      return storedSections;
    }

    const result = [...defaults];

    storedSections.forEach((stored) => {
      const index = result.findIndex((def) => def.id === stored.id);
      if (index !== -1) {
        result[index] = { ...stored };
      }
    });

    return result;
  }
}
